"use client";

import { c as _c } from "react/compiler-runtime";
import * as React from "react";

/** Single row from `media.variants` JSON (dashboard may include on fetch). */
export type MediaVariantRow = {
  url?: string;
  width?: number;
  height?: number;
};

/** Fields returned when picking or loading a library asset (dashboard may enrich). */
export type MediaPickResult = {
  url: string;
  alt?: string;
  /** File Manager `media.id` when image was chosen from the library (usage tracking). */
  mediaId?: string;
  /** Variant map when host loads full media details (custom delivery keys in inspector). */
  variants?: Record<string, MediaVariantRow>;
  /** Library `title` (native `<img title>`). */
  title?: string | null;
  caption?: string | null;
  description?: string | null;
  tags?: string[];
  width?: number | null;
  height?: number | null;
  status?: string;
  securityQuarantinedAt?: string | null;
  /** Short line from latest AI moderation recheck, if any. */
  aiReviewNote?: string | null;
};
export type MediaPickerOpenOptions = {
  accept?: string;
  title?: string;
};
export type MediaPatchPayload = {
  altText?: string | null;
  caption?: string | null;
  title?: string | null;
};
export type MediaPickerHostValue = {
  pickMedia: (options?: MediaPickerOpenOptions) => Promise<MediaPickResult | null>;
  /** Load full metadata for a library id (dashboard only). */
  fetchMediaDetails?: (mediaId: string) => Promise<MediaPickResult | null>;
  /** Persist alt/title/caption on the `media` row (dashboard only). */
  patchMedia?: (mediaId: string, patch: MediaPatchPayload) => Promise<boolean>;
};
const MediaPickerHostContext = React.createContext<MediaPickerHostValue | null>(null);
export function MediaPickerHostProvider(t0) {
  const $ = _c(3);
  const {
    value,
    children
  } = t0;
  let t1;
  if ($[0] !== children || $[1] !== value) {
    t1 = <MediaPickerHostContext.Provider value={value}>{children}</MediaPickerHostContext.Provider>;
    $[0] = children;
    $[1] = value;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}

/**
 * Host-injected File Manager picker (dashboard implements; builder stays decoupled).
 * When no provider is mounted, `pickMedia` resolves to `null`.
 */
export function useMediaPicker() {
  const $ = _c(1);
  const v = React.useContext(MediaPickerHostContext);
  if (!v) {
    let t0;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = {
        pickMedia: _temp
      };
      $[0] = t0;
    } else {
      t0 = $[0];
    }
    return t0;
  }
  return v;
}
async function _temp() {
  return null;
}